import { ReactNode, useEffect, useRef, useState } from "react";
import styles from "./BundlePreviewWrapper.module.css";
import { AvailableVolumeTemplates, trackAbVisitorEvent, updateBundleStats } from "../../constants";
import { VolumeConfigInterface } from "../../Types";
import { hexToHsba } from "../../../utilities";
import config from "../../../../config";
import BundlePreviewFooter from "./BundlePreviewFooter";
import useIsMobile from "../../../../hooks/useIsMobile";
import { addBundleToCart } from "../../RenderOnStore/utilities";
import { InspectorEditableBlock } from "../../CommonFields/InspectorEditableBlock";
import { ADD_TO_CART_CLICKED, executeScopedJS, injectScopedCSS } from "../../../constants";

const BundlePreviewWrapper = ({
    children,
    bundleConfig,
    standAlone,
    totalPrice = null,
    currency,
    money_format,
}: {
    children: ReactNode;
    bundleConfig: VolumeConfigInterface;
    standAlone?: boolean;
    totalPrice?: number | null;
    currency: string;
    money_format: string;
}) => {
    const [loading, setLoading] = useState(false);
    const [finalPrice, setFinalPrice] = useState(0);
    const appName = config.appName;
    const { isMobile } = useIsMobile();
    const bundleWrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (standAlone && bundleConfig?.form_id && bundleConfig?.shop) {
            updateBundleStats({
                id: bundleConfig?.form_id,
                key: "views",
                shop: bundleConfig?.shop,
                appName: appName,
                view_type: 1,
            });

            trackAbVisitorEvent(bundleConfig, "view", 1, appName);
        }
    }, [bundleConfig?.form_id, standAlone]);

    // Simplified: the original switch here had a case per bundle type; VOLUME_DISCOUNT's case was
    // just `setFinalPrice(totalPrice)`, which is the only case relevant once only Volume renders
    // through this file.
    useEffect(() => {
        if (totalPrice != null) {
            setFinalPrice(totalPrice);
        }
    }, [totalPrice]);

    const addToCart = async () => {
        setLoading(true);
        try {
            updateBundleStats({
                id: bundleConfig?.form_id,
                key: "clicks",
                shop: bundleConfig?.shop,
                appName: appName,
                view_type: 1,
            });

            trackAbVisitorEvent(bundleConfig, "click", 1, appName);

            document.dispatchEvent(new CustomEvent(ADD_TO_CART_CLICKED, { detail: {} }));

            await addBundleToCart(
                bundleConfig.products
                    .concat(bundleConfig?.free_gifts || [])
                    .concat(bundleConfig?.bundle_products || [])
                    .concat(bundleConfig?.upsell_products || []),
                {},
                bundleConfig.form_id || "",
                !!bundleConfig?.general_settings?.redirect_to_checkout,
                bundleConfig?.bundle_type,
            );
        } finally {
            setLoading(false);
        }
    };

    const { alpha } = hexToHsba(bundleConfig?.bundles_design?.bundle_widget_bg);

    if (window.kefi_bundles.shop === "nuphy-store.myshopify.com") bundleConfig.max_width = 15000;

    const custom_css = bundleConfig?.bundles_settings?.custom_css || "";
    const custom_js = bundleConfig?.bundles_settings?.custom_js || "";

    useEffect(() => {
        const formId = bundleConfig.form_id || "";

        if (custom_css) {
            injectScopedCSS(custom_css, formId);
        }

        if (custom_js) {
            executeScopedJS(custom_js, formId);
        }

        return () => {
            document.querySelector(`style[data-form-id="${formId}"]`)?.remove();
        };
    }, [bundleConfig.form_id]);

    return (
        <div
            className={styles.wrapperRoot}
            style={{
                justifyContent: standAlone ? bundleConfig?.general_settings?.bundle_alignment : "center",
                // zoom: isMobile ? 0.8 : 1,
            }}
            id={"kefi_" + bundleConfig.form_id}
            data-form-id={bundleConfig.form_id}
        >
            <InspectorEditableBlock
                bundleType={bundleConfig?.bundle_type}
                settingKey="bundlePreviewWrapper"
                wrapWithDiv={true}
            >
                <div
                    ref={bundleWrapperRef}
                    className={`${styles.bundlePreviewWrapper} ${styles.wrapperInner}`}
                    style={{
                        borderRadius:
                            bundleConfig?.bundles_design?.bundle_border_radius ??
                            bundleConfig?.bundles_design?.border_radius,
                        background: bundleConfig?.bundles_design?.bundle_widget_bg,
                        fontFamily:
                            bundleConfig?.bundles_design?.bundle_widget_font_family == "Custom Store Font"
                                ? "inherit"
                                : bundleConfig?.bundles_design?.bundle_widget_font_family,
                        marginTop: standAlone
                            ? bundleConfig?.general_settings?.override_margin_top
                                ? bundleConfig?.general_settings?.margin_top
                                : 20
                            : 0,
                        maxWidth:
                            (bundleConfig?.general_settings?.override_max_width
                                ? bundleConfig?.general_settings?.max_width
                                : bundleConfig?.max_width) !== undefined
                                ? `${
                                      bundleConfig?.general_settings?.override_max_width
                                          ? bundleConfig?.general_settings?.max_width
                                          : bundleConfig?.max_width
                                  }px`
                                : undefined,
                        marginBottom: !standAlone && bundleConfig?.bundles_design?.default_cta ? 0 : 10,
                        marginLeft: !standAlone ? 0 : undefined,
                        marginRight: !standAlone ? 0 : undefined,
                    }}
                    id="bundlePreviewWrapper"
                >
                    <div
                        style={{
                            paddingInline: alpha == 0 ? "0px" : "10px",
                        }}
                        className={styles.prevWrapperChild}
                    >
                        <InspectorEditableBlock settingKey="bundleTitle" bundleType={bundleConfig?.bundle_type}>
                            <div
                                style={{
                                    fontSize:
                                        bundleConfig?.bundles_design?.bundle_title_size <= 50
                                            ? bundleConfig?.bundles_design?.bundle_title_size
                                            : 50,
                                    color: bundleConfig?.bundles_design?.bundle_title_color,
                                    fontWeight: bundleConfig?.bundles_design?.bundle_title_style?.split(" ")[0],
                                    fontStyle: bundleConfig?.bundles_design?.bundle_title_style?.split(" ")[1],
                                }}
                                className={styles.bundleTitle}
                            >
                                {bundleConfig?.show_line_around_title && (
                                    <span
                                        className={styles.titleLineLeft}
                                        style={{
                                            backgroundColor: bundleConfig?.bundles_design?.show_line_around_title_color,
                                        }}
                                    />
                                )}
                                {bundleConfig.title}
                                {bundleConfig?.show_line_around_title && (
                                    <span
                                        className={styles.titleLineRight}
                                        style={{
                                            backgroundColor: bundleConfig?.bundles_design?.show_line_around_title_color,
                                        }}
                                    />
                                )}
                            </div>
                        </InspectorEditableBlock>

                        <div
                            className={`${styles.bundlePreviewChildrenWrapper}`}
                            style={{
                                ...(bundleConfig?.bundles_design?.hover_effect != "none" &&
                                    ![
                                        AvailableVolumeTemplates.HORIZONTAL_BASIC,
                                        AvailableVolumeTemplates.HORIZONTAL_MODERN,
                                    ].includes(bundleConfig?.selected_template_volume) && { paddingInline: 10 }),
                            }}
                        >
                            {children}
                            <BundlePreviewFooter
                                standAlone={!!standAlone}
                                totalPrice={totalPrice as unknown as number}
                                finalPrice={finalPrice}
                                money_format={money_format}
                                currency={currency}
                                bundleConfig={bundleConfig}
                                loading={loading}
                                addToCart={addToCart}
                                isMobile={isMobile}
                            />
                        </div>
                    </div>
                </div>
            </InspectorEditableBlock>
        </div>
    );
};

export default BundlePreviewWrapper;
